import React, { ReactNode, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { TranslationMapType } from '@/modules/common/types/i18n';

import logger from '@/modules/common/helpers/logger';
import { translationsSelector } from '@/modules/common/redux/selectors/i18n';

type RichValue = string | number | ReactNode | (() => ReactNode);
type RichValues = Record<string, RichValue>;

export type Translator = {
  (key: string, values?: Record<string, string | number>): string;
  jsx(key: string, values: RichValues): ReactNode[];
};

type TranslationEntries<T extends Record<string, string>> = {
  [K in keyof T]: {
    text: (values?: Record<string, string | number>) => string;
    jsx: (values: RichValues) => ReactNode[];
  };
};

export function interpolateRich(template: string, values: RichValues = {}): ReactNode[] {
  const parts: ReactNode[] = [];
  const lines = template.split('\n');

  lines.forEach((line, lineIndex) => {
    const regex = /{{\s*(\w+)\s*}}/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(line))) {
      const [placeholder, key] = match;
      const { index } = match;

      if (index > lastIndex) {
        parts.push(line.slice(lastIndex, index));
      }

      const value = values[key];

      if (typeof value === 'function') {
        parts.push(value());
      }
      if (value !== undefined) {
        parts.push(String(value));
      } else {
        parts.push(placeholder);
      }

      lastIndex = index + placeholder.length;
    }

    if (lastIndex < line.length) {
      parts.push(line.slice(lastIndex));
    }

    if (lineIndex < lines.length - 1) {
      parts.push(<br key={`br-${lineIndex}`} />);
    }
  });

  return parts;
}

function interpolateString(template: string, values: Record<string, string | number> = {}): string {
  return template.replace(/{{\s*(\w+)\s*}}/g, (_, key) => (key in values ? String(values[key]) : `{{${key}}}`));
}

export function createTranslator(translations: TranslationMapType): Translator {
  const t = ((key: keyof TranslationMapType, values?: Record<string, string | number>) => {
    const template = translations[key];

    if (template === undefined) {
      if (process.env.NODE_ENV === 'development') {
        logger.error(`[i18n] Missing translation key: "${key}"`);
      }

      return key;
    }

    return interpolateString(template, values);
  }) as Translator;

  t.jsx = (key: keyof TranslationMapType, values) => {
    const template = translations[key];
    if (template === undefined) {
      if (process.env.NODE_ENV === 'development') {
        logger.error(`[i18n] Missing translation key: "${key}"`);
      }
      return [key];
    }
    return interpolateRich(template, values);
  };

  return t;
}

export function makeTranslate<T extends TranslationMapType>(translations: T): TranslationEntries<T> {
  const t = createTranslator(translations);
  const entries = {} as TranslationEntries<T>;

  // eslint-disable-next-line guard-for-in
  for (const key in translations) {
    entries[key] = {
      text: (values) => t(key, values),
      jsx: (values) => t.jsx(key, values),
    };
  }

  return entries;
}

export function useTranslate() {
  const translations = useSelector(translationsSelector);

  return useCallback(() => makeTranslate(translations), [translations])();
}
